import { ButtonLooks, FontIcons, IconButton, StandardTextfield } from '@brandfolder/react';
import { slugify } from '@brandfolder/utilities';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useRef, useState } from 'react';
import styled from 'styled-components';

import { ConfirmDeleteDialog } from '@components/dialogs';
import { BlockContext } from '@context/blocks';
import { PagesContext } from '@context/pages';
import { PublishContext } from '@context/publish';
import { BlockTypes } from '@enums/blocks';
import { Block, BlockHtml } from '@typings/block';
import {
  deleteBlock,
  getBlockDataIsSection,
  getSectionIsDuplicate,
  getSectionIsReserved,
  updateBlock
} from '@utilities/block';

import { PagesDragIcon } from './pages-drag-icon';
import { PagesEdit } from './pages-edit';
import { PagesName } from './pages-name';

interface PagesEditSectionProps extends HTMLAttributes<HTMLDivElement> {
  block: Block;
  sectionBlocks: Block[];
}

const UnstyledPagesEditSection: FunctionComponent<PagesEditSectionProps> = (props) => {
  const { block, sectionBlocks, ...otherProps } = props;
  const { data, key, pageKey } = block;

  const ref = useRef<HTMLInputElement | null>(null);

  const { blocks, initialBlocks, sections, setBlocks } = useContext(BlockContext);
  const { editingSections, setEditingSections } = useContext(PagesContext);
  const { setPublish } = useContext(PublishContext);

  const [edit, setEdit] = useState(false);
  const [open, setOpen] = useState(false);

  const sectionData = getBlockDataIsSection(data) ? data : null;

  if (!sectionData) return null;

  const { id, name } = sectionData;

  const only = sectionBlocks.length === 1;
  const sectionDuplicate = edit ? getSectionIsDuplicate(id, key, pageKey, sections) : false;
  const sectionReserved = edit ? getSectionIsReserved(name) : false;

  const updateUntouchedSectionHeading = (value: string) => {
    const sectionBlocks = blocks.filter((b) => key === b.parentKey && b.type === BlockTypes.Text);

    if (
      sectionBlocks.length === 1 &&
      (sectionBlocks[0].data as BlockHtml).html.startsWith('<h2>') &&
      (sectionBlocks[0].data as BlockHtml).html.endsWith('</h2>')
    ) {
      setBlocks([
        ...updateBlock(
          {
            ...sectionBlocks[0],
            data: { html: `<h2>${value}</h2>` }
          },
          blocks
        )
      ]);
    }
  };

  const editSection = () => {
    setEditingSections([...editingSections, key]);
    setEdit(true);
  };

  const stopEditingSection = (publish?: boolean, reset?: boolean) => {
    if (publish) {
      setPublish(true);
    }
    if (reset) {
      setBlocks(initialBlocks);
    }

    setEditingSections(editingSections.filter((k) => k !== key));
    setEdit(false);
  };

  return (
    <PagesEdit {...otherProps} only={only} section>
      {edit && (
        <div
          className={classNames({
            'page-edit__wrapper': true,
            'page-edit__wrapper--editing': true,
            'page-edit__wrapper--error': sectionDuplicate || sectionReserved,
            'page-edit__wrapper--only': only
          })}
        >
          <PagesDragIcon only={only} />
          <div className="name">
            <StandardTextfield
              error={
                sectionDuplicate
                  ? 'Section must be unique'
                  : sectionReserved
                  ? `${name} is a reserved name`
                  : undefined
              }
              id={`section-name-${key}`}
              label="Name"
              onBlur={(e) => updateUntouchedSectionHeading(e.target.value)}
              onChange={(e) => {
                const { value } = e.target;

                setBlocks([
                  ...updateBlock(
                    {
                      ...block,
                      data: { ...sectionData, id: slugify(value), name: value }
                    },
                    blocks
                  )
                ]);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  ref.current?.blur();
                  stopEditingSection(true);
                }
              }}
              value={name}
              ref={ref}
              showLabel={false}
              textfieldProps={{
                autoFocus: true
              }}
            />
          </div>
          <div>
            <IconButton
              disabled={sectionDuplicate || sectionReserved}
              icon={FontIcons.CheckMark}
              label="Save section"
              look={ButtonLooks.Tertiary}
              onClick={() => {
                stopEditingSection(true);
              }}
            />
            <IconButton
              icon={FontIcons.Close}
              label="Cancel editing section"
              look={ButtonLooks.Tertiary}
              onClick={() => {
                stopEditingSection(undefined, true);
              }}
            />
          </div>
        </div>
      )}
      {!edit && (
        <div
          className={classNames({
            'page-edit__wrapper': true,
            'page-edit__wrapper--viewing': true,
            'page-edit__wrapper--only': only
          })}
        >
          <PagesDragIcon only={only} />
          <PagesName name={name} />
          <div>
            <IconButton
              icon={edit ? FontIcons.Close : FontIcons.Edit}
              label="Edit section"
              look={ButtonLooks.Tertiary}
              onClick={() => {
                editSection();
              }}
            />
            <IconButton
              icon={FontIcons.Trash}
              label="Delete section"
              look={ButtonLooks.Tertiary}
              onClick={() => setOpen(true)}
            />
          </div>
          <ConfirmDeleteDialog
            block={block}
            id={`confirm-delete-${key}`}
            onCancel={() => setOpen(false)}
            onConfirm={() => {
              setBlocks([...deleteBlock(block, blocks, pageKey)]);
              setPublish(true);
              setOpen(false);
            }}
            open={open}
            setOpen={setOpen}
          />
        </div>
      )}
    </PagesEdit>
  );
};

const StyledPagesEditSection = styled(UnstyledPagesEditSection)`
  .page-edit__wrapper {
    align-items: center;
    display: flex;
    flex: 1;

    &.page-edit__wrapper--error {
      align-items: flex-end;
    }
  }

  .name {
    align-self: center;
    flex: 1;
    margin-right: 5px;
  }

  .bf-textfield__textfield {
    min-height: 0;
    padding: 4px 8px;
  }

  .confirm {
    margin-bottom: 32px;
  }

  .button-group {
    display: flex;
    justify-content: center;
  }

  .cancel {
    margin-right: 12px;
  }
`;

export const PagesEditSection: FunctionComponent<PagesEditSectionProps> = (props) => {
  return <StyledPagesEditSection {...props} />;
};
